import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Code, Copy, Check, Eye, EyeOff, ChevronDown, ChevronUp, Search, X, Lightbulb, Settings, ArrowRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { toast } from "sonner";
import { ToggleGroup, ToggleGroupItem } from "@shadcn-in-nx/ui/toggle-group";
import Prism from "prismjs";
import "prismjs/themes/prism.css"; // Light theme
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-python";
import { Bluelagoon } from "@bluelagoon";
import ApiSecurityTester from "./ApiSecurityTester";
import { useChatContext } from "../ChatContext";

function APIsPanel({
    components,
    workflowCustomDomain,
    workflowInternalDomain,
    userServiceKey,
    isFunctionAPIEnabled,
    onRefresh,
    onAuditKeyView,
}) {
    const { t } = useTranslation();
    const {
        setPrefillMessage,
        setAppViewMode,
        navigateToSettings,
    } = useChatContext();
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState("python");
    const [isKeyVisible, setIsKeyVisible] = useState(false);
    const [expandedFunctions, setExpandedFunctions] = useState(() => new Set());
    const [searchQuery, setSearchQuery] = useState("");

    const toggleFunctionExpanded = useCallback((id) => {
        setExpandedFunctions((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const handleFixInChat = useCallback(
        (func) => {
            const message =
                t("appPreview.fixSecurityPrompt", { name: func.functionName }) ||
                `The API endpoint \`/api/${func.functionName}\` (file: \`functions/${func.functionName}.js\`) does not check authentication, so anyone with the URL can call it. Please add a guard at the top of the function: return a 401 response unless \`isInServiceRole\` is true or \`User.me()\` returns a valid user. Keep the existing logic intact.`;
            setPrefillMessage(message);
            setAppViewMode("chat");
        },
        [setPrefillMessage, setAppViewMode, t]
    );

    const handleAskAiToCreateFunction = useCallback(() => {
        const message =
            t("appPreview.createFunctionPrompt") ||
            "Create a new backend function for this app. Add a new `.js` file under the `functions/` folder so it gets exposed as a callable API endpoint. Ask me what the function should do if you need clarification, otherwise pick a useful starter function based on the app's purpose and add basic auth (return 401 unless `isInServiceRole` is true or `User.me()` returns a valid user).";
        setPrefillMessage(message);
        setAppViewMode("chat");
    }, [setPrefillMessage, setAppViewMode, t]);

    // Refresh components when panel mounts
    useEffect(() => {
        if (onRefresh) onRefresh();
    }, [onRefresh]);

    const handleToggleKeyVisibility = useCallback(() => {
        if (!isKeyVisible && onAuditKeyView) {
            onAuditKeyView();
        }
        setIsKeyVisible((prev) => !prev);
    }, [isKeyVisible, onAuditKeyView]);

    // Filter and extract function components
    const functions = useMemo(() => {
        if (!components || !Array.isArray(components)) return [];

        return components
            .filter((component) => component.type === "FUNCTION")
            .map((component) => {
                // Extract function name from filePath
                // Examples: "functions/sendEmail.js" -> "sendEmail"
                //          "api/getUsers.js" -> "getUsers"
                //          "api/functions/sendEmail.js" -> "functions/sendEmail"
                let functionName = component.name;

                if (component.filePath) {
                    // Remove leading "functions/" or "api/" if present
                    let path = component.filePath;
                    if (path.startsWith("functions/")) {
                        path = path.substring("functions/".length);
                    } else if (path.startsWith("api/")) {
                        path = path.substring("api/".length);
                    }

                    // Remove extension (.js, .ts, etc.)
                    functionName = path.replace(/\.[^.]+$/, "");
                }

                return {
                    ...component,
                    functionName,
                };
            })
            .filter((func) => func.functionName); // Filter out invalid function names
    }, [components]);

    const filteredFunctions = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return functions;
        return functions.filter((f) =>
            f.functionName.toLowerCase().includes(q)
        );
    }, [functions, searchQuery]);

    // Highlight code blocks when functions or selected language changes
    useEffect(() => {
        if (functions.length > 0) {
            // Small delay to ensure DOM is updated
            setTimeout(() => {
                Prism.highlightAll();
            }, 100);
        }
    }, [functions, selectedLanguage, expandedFunctions]);

    // Determine the base URL for API endpoints. Mirrors the iframe's logic
    // (Bluelagoon.getComponentServerUrl) so the panel always shows a usable
    // URL — falling back to the global component-server endpoint when no
    // custom or internal domain is configured on the workflow.
    const baseUrl = useMemo(() => {
        const { componentServerUrl } = Bluelagoon.getComponentServerUrl(
            workflowInternalDomain,
            workflowCustomDomain
        );
        return componentServerUrl || "";
    }, [workflowCustomDomain, workflowInternalDomain]);

    const copyToClipboard = async (text, index) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedIndex(index);
            toast.success(
                t("appPreview.codeCopied") || "Code copied to clipboard"
            );
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (error) {
            toast.error(t("appPreview.copyFailed") || "Failed to copy code");
        }
    };

    // Treat system-generated edit/commit log strings as missing descriptions.
    // The component's `description` field sometimes gets overwritten by the
    // code-edit pipeline with messages like "Edit file: replaced 1 occurrence".
    const isMeaningfulDescription = (desc) => {
        if (!desc || typeof desc !== "string") return false;
        const t = desc.trim();
        if (!t) return false;
        if (/^Edit\s+file\s*:/i.test(t)) return false;
        if (
            /^(Replaced|Updated|Added|Removed|Wrote|Inserted|Deleted)\s+\d+\s+(occurrence|instance|line|byte|file|character)/i.test(
                t
            )
        ) {
            return false;
        }
        return true;
    };

    const generateCodeExamples = (functionName) => {
        const endpoint = `${baseUrl}/api/${functionName}`;
        const serviceKey = isKeyVisible ? (userServiceKey || "") : "••••••••••••••••••••••••";

        const pythonCode = `import requests

url = "${endpoint}"
headers = {
    "Content-Type": "application/json",
    "x-server-key": "${serviceKey}"
}
data = {
    # Add your request body here
    # Example: "key": "value"
}

response = requests.post(url, json=data, headers=headers)
print(response.json())`;

        const javascriptCode = `const response = await fetch("${endpoint}", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
        "x-server-key": "${serviceKey}"
    },
    body: JSON.stringify({
        // Add your request body here
        // Example: key: "value"
    })
});

const data = await response.json();
console.log(data);`;

        const curlCode = `curl -X POST "${endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "x-server-key: ${serviceKey}" \\
  -d '{
    "key": "value"
  }'`;

        return { pythonCode, javascriptCode, curlCode };
    };

    if (!baseUrl) {
        return (
            <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <p className="text-sm text-gray-500 text-center py-8">
                            {t("appPreview.noDomainConfigured") ||
                                "No domain configured for this app"}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (functions.length === 0) {
        const backendDisabled = isFunctionAPIEnabled === false;
        return (
            <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                            <Code className="w-5 h-5 text-blue-600" />
                            <h2 className="text-base font-semibold text-gray-900">
                                {t("appPreview.apiReference") || "API Reference"}
                            </h2>
                        </div>
                        <div className="px-6 py-12 flex flex-col items-center text-center">
                            <div
                                className={`w-14 h-14 rounded-full flex items-center justify-center mb-5 ${
                                    backendDisabled
                                        ? "bg-amber-50 ring-1 ring-amber-100"
                                        : "bg-blue-50 ring-1 ring-blue-100"
                                }`}
                            >
                                {backendDisabled ? (
                                    <Settings className="w-7 h-7 text-amber-600" />
                                ) : (
                                    <Sparkles className="w-7 h-7 text-blue-600" />
                                )}
                            </div>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">
                                {backendDisabled
                                    ? t("appPreview.backendDisabledTitle") ||
                                      "Backend functions are off"
                                    : t("appPreview.noFunctionsTitle") ||
                                      "No API functions yet"}
                            </h3>
                            <p className="text-sm text-gray-500 max-w-md leading-relaxed mb-6">
                                {backendDisabled
                                    ? t("appPreview.backendDisabledDescription") ||
                                      "Turn on Function API in Settings to expose your backend functions as callable API endpoints for this app."
                                    : t("appPreview.noFunctionsHint") ||
                                      "Create a file inside the functions/ folder in the Editor. Each file becomes a callable API endpoint authenticated with your service key."}
                            </p>
                            {backendDisabled ? (
                                <Button
                                    onClick={navigateToSettings}
                                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                                >
                                    <Settings className="w-4 h-4 mr-2" />
                                    {t("appPreview.enableInSettings") ||
                                        "Enable in Settings"}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            ) : (
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <Button
                                        onClick={handleAskAiToCreateFunction}
                                        className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                                    >
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        {t("appPreview.askAiToCreateFunction") ||
                                            "Ask AI to create a function"}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={navigateToSettings}
                                        className="text-gray-600 hover:text-gray-900"
                                    >
                                        {t("appPreview.checkBackendSettings") ||
                                            "Check backend settings"}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Code className="w-5 h-5 text-blue-600" />
                        {t("appPreview.apiReference") || "API Reference"}
                    </h2>
                    <p className="text-sm text-gray-500">
                        {t("appPreview.apiReferenceDescription") ||
                            "Available API endpoints for this app. Use the x-server-key header for authentication."}
                    </p>
                    {userServiceKey && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-xs font-medium text-blue-900">
                                    {t("appPreview.userServiceKey") ||
                                        "User Service Key"}
                                    :
                                </p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs text-blue-700 hover:text-blue-900"
                                    onClick={handleToggleKeyVisibility}
                                >
                                    {isKeyVisible ? (
                                        <>
                                            <EyeOff className="w-3 h-3 mr-1" />
                                            {t("common.hide") || "Hide"}
                                        </>
                                    ) : (
                                        <>
                                            <Eye className="w-3 h-3 mr-1" />
                                            {t("common.reveal") || "Reveal"}
                                        </>
                                    )}
                                </Button>
                            </div>
                            <code className="text-xs text-blue-700 break-all">
                                {isKeyVisible
                                    ? userServiceKey
                                    : "••••••••••••••••••••••••"}
                            </code>
                        </div>
                    )}
                    <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-2.5">
                        <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-gray-700 leading-relaxed">
                            <span className="font-semibold text-gray-900">
                                {t("appPreview.usageTipTitle") || "Tip"}:
                            </span>{" "}
                            {t("appPreview.usageTipBody") ||
                                "Use these endpoints in another app or automation — type @ in chat, pick Functions, then choose the app and the function."}
                        </p>
                    </div>
                </div>

                {/* Security Tester - one-click static + live check for auth guards */}
                <ApiSecurityTester
                    functions={functions}
                    baseUrl={baseUrl}
                    onFixInChat={handleFixInChat}
                />

                {/* Search bar — filters the function reference list below */}
                {functions.length > 0 && (
                    <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={
                                t("appPreview.searchFunctions") ||
                                "Search functions"
                            }
                            className="w-full bg-white border border-gray-200 rounded-lg shadow-sm pl-9 pr-9 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery("")}
                                aria-label={t("common.clear") || "Clear"}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 rounded"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                )}

                {/* Language Selection Toggle Group - Shared across all functions */}
                {functions.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-0 overflow-hidden">
                        <ToggleGroup
                            type="single"
                            variant="outline"
                            value={selectedLanguage}
                            onValueChange={(value) => {
                                if (value) {
                                    setSelectedLanguage(value);
                                }
                            }}
                            className="w-full flex"
                        >
                            <ToggleGroupItem
                                value="python"
                                aria-label="Python"
                                className="flex-1 rounded-none border-0 border-r border-gray-200 first:border-l-0 data-[state=on]:bg-blue-50 data-[state=on]:text-blue-700 data-[state=on]:font-semibold"
                            >
                                Python
                            </ToggleGroupItem>
                            <ToggleGroupItem
                                value="javascript"
                                aria-label="JavaScript"
                                className="flex-1 rounded-none border-0 border-r border-gray-200 data-[state=on]:bg-blue-50 data-[state=on]:text-blue-700 data-[state=on]:font-semibold"
                            >
                                JavaScript
                            </ToggleGroupItem>
                            <ToggleGroupItem
                                value="curl"
                                aria-label="cURL"
                                className="flex-1 rounded-none border-0 data-[state=on]:bg-blue-50 data-[state=on]:text-blue-700 data-[state=on]:font-semibold"
                            >
                                cURL
                            </ToggleGroupItem>
                        </ToggleGroup>
                    </div>
                )}

                {/* No-results placeholder when search returns empty */}
                {searchQuery && filteredFunctions.length === 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
                        <p className="text-sm text-gray-500">
                            {t("appPreview.noFunctionsMatch") ||
                                "No functions match your search"}
                        </p>
                    </div>
                )}

                {/* Functions List */}
                {filteredFunctions.map((func, index) => {
                    const { pythonCode, javascriptCode, curlCode } =
                        generateCodeExamples(func.functionName);
                    const codeBlocks = [
                        {
                            name: "Python",
                            code: pythonCode,
                            language: "python",
                            value: "python",
                        },
                        {
                            name: "JavaScript",
                            code: javascriptCode,
                            language: "javascript",
                            value: "javascript",
                        },
                        {
                            name: "cURL",
                            code: curlCode,
                            language: "bash",
                            value: "curl",
                        },
                    ];

                    return (
                        <div
                            key={func.id}
                            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                        >
                            <div className="mb-4">
                                <div className="flex items-start justify-between gap-3">
                                    <h3 className="text-base font-semibold text-gray-900">
                                        {func.functionName}
                                    </h3>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs shrink-0 text-gray-600 hover:text-gray-900"
                                        onClick={() =>
                                            toggleFunctionExpanded(func.id)
                                        }
                                    >
                                        {expandedFunctions.has(func.id) ? (
                                            <>
                                                <ChevronUp className="w-3 h-3 mr-1" />
                                                {t("appPreview.hideExample") ||
                                                    "Hide example"}
                                            </>
                                        ) : (
                                            <>
                                                <ChevronDown className="w-3 h-3 mr-1" />
                                                {t("appPreview.showExample") ||
                                                    "Show example"}
                                            </>
                                        )}
                                    </Button>
                                </div>
                                {isMeaningfulDescription(func.description) && (
                                    <p className="text-sm text-gray-600 mt-1">
                                        {func.description}
                                    </p>
                                )}
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
                                        POST
                                    </span>
                                    <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700 break-all">
                                        {baseUrl}/api/{func.functionName}
                                    </code>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 shrink-0 text-gray-500 hover:text-gray-900"
                                        aria-label={
                                            t("appPreview.copyUrl") ||
                                            "Copy URL"
                                        }
                                        onClick={() =>
                                            copyToClipboard(
                                                `${baseUrl}/api/${func.functionName}`,
                                                `${index}-url`
                                            )
                                        }
                                    >
                                        {copiedIndex === `${index}-url` ? (
                                            <Check className="w-3 h-3" />
                                        ) : (
                                            <Copy className="w-3 h-3" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            {/* Code Examples - Collapsed by default, shown when expanded */}
                            {expandedFunctions.has(func.id) && (
                            <div className="mt-4">
                                {codeBlocks
                                    .filter(
                                        (block) =>
                                            block.value === selectedLanguage
                                    )
                                    .map((block) => {
                                        const uniqueIndex = `${index}-${block.value}`;
                                        const isCopied =
                                            copiedIndex === uniqueIndex;

                                        return (
                                            <div
                                                key={block.value}
                                                className="relative"
                                            >
                                                <div className="flex items-center justify-end mb-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-3 text-xs"
                                                        onClick={() =>
                                                            copyToClipboard(
                                                                block.code,
                                                                uniqueIndex
                                                            )
                                                        }
                                                    >
                                                        {isCopied ? (
                                                            <>
                                                                <Check className="w-3 h-3 mr-1" />
                                                                {t(
                                                                    "common.copied"
                                                                ) || "Copied"}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Copy className="w-3 h-3 mr-1" />
                                                                {t(
                                                                    "common.copy"
                                                                ) || "Copy"}
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                                <pre className="bg-white border border-slate-200 shadow-sm p-4 rounded-lg overflow-x-auto text-xs font-mono leading-relaxed">
                                                    <code
                                                        className={`language-${block.language}`}
                                                    >
                                                        {block.code}
                                                    </code>
                                                </pre>
                                            </div>
                                        );
                                    })}
                            </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default APIsPanel;
